/**
 * BottomSheet Component
 *
 * A mobile-friendly bottom sheet/drawer component with:
 * - Draggable handle for resizing
 * - Snap points (25%, 50%, 90%)
 * - Smooth animations
 * - Backdrop dismiss
 * - Keyboard handling
 *
 * Usage:
 *   <BottomSheet
 *     isOpen={isOpen}
 *     onClose={handleClose}
 *     title="Settings"
 *     snapPoints={[0.25, 0.5, 0.9]}
 *     initialSnap={0.5}
 *   >
 *     <BottomSheet.Content>
 *       Content here
 *     </BottomSheet.Content>
 *   </BottomSheet>
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useIsMobile, usePrefersReducedMotion } from '@/hooks/useMediaQuery';

export interface BottomSheetProps {
  /** Whether sheet is open */
  isOpen: boolean;
  /** Callback when sheet should close */
  onClose: () => void;
  /** Sheet title */
  title?: string;
  /** Available snap points (0-1, percentage of screen height) */
  snapPoints?: number[];
  /** Initial snap point index */
  initialSnap?: number;
  /** Show drag handle */
  showHandle?: boolean;
  /** Show close button */
  showCloseButton?: boolean;
  /** Close on backdrop click */
  closeOnBackdrop?: boolean;
  /** Children content */
  children: React.ReactNode;
  /** Additional class names */
  className?: string;
}

export const BottomSheet: React.FC<BottomSheetProps> & {
  Content: typeof BottomSheetContent;
} = ({
  isOpen,
  onClose,
  title,
  snapPoints = [0.25, 0.5, 0.9],
  initialSnap = 1,
  showHandle = true,
  showCloseButton = true,
  closeOnBackdrop = true,
  children,
  className = '',
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number>(0);
  const currentHeight = useRef<number>(0);
  const [snapIndex, setSnapIndex] = useState(initialSnap);
  const [isDragging, setIsDragging] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const isMobile = useIsMobile();
  const prefersReducedMotion = usePrefersReducedMotion();

  // Calculate height based on snap point
  const getHeightFromSnap = useCallback((index: number) => {
    const snapPoint = snapPoints[index] || snapPoints[snapPoints.length - 1];
    return Math.round(window.innerHeight * snapPoint);
  }, [snapPoints]);

  // Find nearest snap point
  const findNearestSnap = useCallback((currentY: number) => {
    const heights = snapPoints.map((_, i) => getHeightFromSnap(i));
    let nearestIndex = 0;
    let minDiff = Math.abs(heights[0] - currentY);

    heights.forEach((height, index) => {
      const diff = Math.abs(height - currentY);
      if (diff < minDiff) {
        minDiff = diff;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }, [snapPoints, getHeightFromSnap]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset snap index when opening
  useEffect(() => {
    if (isOpen) {
      setSnapIndex(initialSnap);
      setTranslateY(0);
    }
  }, [isOpen, initialSnap]);

  // Touch/Mouse handlers for dragging
  const handleDragStart = useCallback((clientY: number) => {
    setIsDragging(true);
    dragStartY.current = clientY;
    currentHeight.current = getHeightFromSnap(snapIndex);
  }, [snapIndex, getHeightFromSnap]);

  const handleDragMove = useCallback((clientY: number) => {
    if (!isDragging) return;

    const deltaY = dragStartY.current - clientY;
    const newHeight = currentHeight.current + deltaY;
    const maxHeight = window.innerHeight * 0.95;
    const minHeight = window.innerHeight * 0.1;

    // Clamp height
    const clampedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
    const targetHeight = getHeightFromSnap(snapIndex);
    setTranslateY(targetHeight - clampedHeight);
  }, [isDragging, snapIndex, getHeightFromSnap]);

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);
    const targetHeight = getHeightFromSnap(snapIndex);
    const actualHeight = targetHeight - translateY;

    // If dragged down below minimum, close
    if (actualHeight < window.innerHeight * 0.15) {
      onClose();
      return;
    }

    // Find and snap to nearest point
    const newSnapIndex = findNearestSnap(actualHeight);
    setSnapIndex(newSnapIndex);
    setTranslateY(0);
  }, [isDragging, snapIndex, translateY, getHeightFromSnap, findNearestSnap, onClose]);

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Mouse event handlers (for testing on desktop)
  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientY);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientY);
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  if (!isOpen) return null;

  const sheetHeight = getHeightFromSnap(snapIndex);
  const animationClass = prefersReducedMotion ? '' : 'transition-transform duration-300 ease-out';

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[1040] bg-black/50 backdrop-blur-sm ${prefersReducedMotion ? '' : 'animate-fadeIn'}`}
        onClick={closeOnBackdrop ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`
          fixed left-0 right-0 bottom-0 z-[1050]
          bg-card-light dark:bg-card-dark
          rounded-t-2xl shadow-2xl
          flex flex-col
          ${isDragging ? '' : animationClass}
          ${className}
        `.trim()}
        style={{
          height: `${sheetHeight}px`,
          transform: `translateY(${translateY}px)`,
          maxHeight: '95vh',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'bottomsheet-title' : undefined}
      >
        {/* Drag Handle */}
        {showHandle && (
          <div
            className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
          >
            <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
          </div>
        )}

        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-4 py-2 border-b border-border-light dark:border-border-dark">
            {title && (
              <h2
                id="bottomsheet-title"
                className="text-lg font-semibold text-ink"
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-2.5 rounded-lg text-ink-3 hover:text-ink-2 hover:bg-inset transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Content */}
        {children}
      </div>
    </>
  );
};

// BottomSheet Content subcomponent
export interface BottomSheetContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const BottomSheetContent: React.FC<BottomSheetContentProps> = ({
  children,
  className = '',
  ...props
}) => {
  return (
    <div
      className={`flex-1 overflow-y-auto overscroll-contain px-4 py-3 pb-safe ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
};

// Attach subcomponents
BottomSheet.Content = BottomSheetContent;

export default BottomSheet;
